import { useState, useEffect } from 'react'
import { Settings2, Monitor, Sun, Moon } from 'lucide-react'
import { Theme } from '../lib/types'

export function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>('system')
  const [isExpanded, setIsExpanded] = useState(false)

  useEffect(() => {
    // Check URL params first
    const params = new URLSearchParams(window.location.search)
    const themeParam = params.get('theme') as Theme | null
    
    if (themeParam && ['light', 'dark', 'system'].includes(themeParam)) {
      setTheme(themeParam)
      applyTheme(themeParam)
    } else {
      // If no URL param, check localStorage or default to system
      const savedTheme = localStorage.getItem('theme') as Theme | null
      const initialTheme = savedTheme || 'system'
      setTheme(initialTheme)
      applyTheme(initialTheme)
    }
  }, [])

  const applyTheme = (newTheme: Theme) => {
    const root = window.document.documentElement
    root.classList.remove('light', 'dark')

    if (newTheme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
      root.classList.add(systemTheme)
    } else {
      root.classList.add(newTheme)
    }
  }

  const changeTheme = (newTheme: Theme) => {
    setTheme(newTheme)
    localStorage.setItem('theme', newTheme)
    applyTheme(newTheme)

    // Update URL parameter
    const url = new URL(window.location.href)
    if (newTheme === 'system') {
      url.searchParams.delete('theme')
    } else {
      url.searchParams.set('theme', newTheme)
    }
    window.history.replaceState({}, '', url.toString())
  }

  const toggleExpanded = () => setIsExpanded(!isExpanded)

  return (
    <div className="relative">
      <button
        onClick={toggleExpanded}
        className="p-2 rounded-md bg-white/80 backdrop-blur-sm shadow-lg text-gray-800 hover:bg-gray-200 transition-colors"
        aria-label="Toggle theme settings"
      >
        <Settings2 size={20} />
      </button>
      {isExpanded && (
        <div className="absolute right-0 mt-2 p-2 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg flex space-x-2">
          <button
            onClick={() => changeTheme('system')}
            className={`p-2 rounded-md ${theme === 'system' ? 'bg-gray-200' : 'hover:bg-gray-100'}`}
            aria-label="System theme"
          >
            <Monitor size={20} />
          </button>
          <button
            onClick={() => changeTheme('light')}
            className={`p-2 rounded-md ${theme === 'light' ? 'bg-gray-200' : 'hover:bg-gray-100'}`}
            aria-label="Light theme"
          >
            <Sun size={20} />
          </button>
          <button
            onClick={() => changeTheme('dark')}
            className={`p-2 rounded-md ${theme === 'dark' ? 'bg-gray-200' : 'hover:bg-gray-100'}`}
            aria-label="Dark theme"
          >
            <Moon size={20} />
          </button>
        </div>
      )}
    </div>
  )
} 